// İSG uzmanı portalı — yönetim (admin) erişim sondası (route guard UX).
//
// Token'da `isSystemAdmin` claim'i YOKTUR (api-contract §5.1 — yalnız `roleUuid[]`);
// bu yüzden "bu kullanıcı admin mi" sorusunun **tek doğru otoritesi backend'dir**
// ([ModuleAction("admin-*")] → grant'sız principal 403). Sonda: modül kataloğunu
// (`GET /admin/modules`, `admin-roles/get` yetkili) çeker.
//   - 2xx  → erişim var (granted) — katalog da döner (matris ekranı yeniden kullanır)
//   - 403  → yetki yok (forbidden) → gizli menü + 403 sayfası
//   - diğer (503/ağ) → servis kullanılamıyor (unavailable) → yeniden dene
// Sorgu anahtarı matris kataloğuyla PAYLAŞILIR (['admin','modules']) — tek istek.

import { ApiError, type ModuleDto, adminApi } from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';

export const adminModulesKey = ['admin', 'modules'] as const;

export type AdminAccessStatus = 'loading' | 'granted' | 'forbidden' | 'unavailable';

export interface AdminAccess {
  status: AdminAccessStatus;
  /** Modül kataloğu (yalnız `granted` iken dolu). */
  catalog: ModuleDto[] | undefined;
  /** `unavailable` durumunda yeniden denemek için. */
  refetch: () => void;
}

/**
 * Yönetim erişimini backend sondasıyla belirler. Oturum açık (RequireAuth altı)
 * varsayılır. 4xx'te retry yok (anında sonuç); 403 ≠ 503 ayrımı korunur.
 */
export function useAdminAccess(): AdminAccess {
  const query = useQuery({
    queryKey: adminModulesKey,
    queryFn: () => adminApi.getModuleCatalog(),
    retry: false,
    staleTime: 5 * 60_000,
  });

  let status: AdminAccessStatus;
  if (query.isPending) {
    status = 'loading';
  } else if (query.isSuccess) {
    status = 'granted';
  } else if (query.error instanceof ApiError && query.error.statusCode === 403) {
    status = 'forbidden';
  } else {
    status = 'unavailable';
  }

  return { status, catalog: query.data, refetch: () => void query.refetch() };
}
